import React from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { getPlatform, getPlatformColor } from '../config/platforms';

export type ContentVariants = Record<string, string>;

interface Props {
  /** Platforms the post targets. */
  platforms: string[];
  /** The shared caption every platform without its own version receives. */
  content: string;
  variants: ContentVariants;
  onChange: (next: ContentVariants) => void;
}

/**
 * The text one platform will actually receive — its own version, or the shared caption.
 *
 * Mirrors `contentFor()` on the server. Every length check in the UI must go through this, or
 * it measures the shared caption against a limit the platform is not actually held to.
 */
export const textFor = (
  platformId: string,
  content: string,
  variants: ContentVariants
): string => {
  const override = variants[platformId];
  return override && override.trim() ? override : content;
};

/** Platforms whose resolved text is over their own limit. */
export const overLimitPlatforms = (
  platforms: string[],
  content: string,
  variants: ContentVariants
) =>
  platforms
    .map(p => getPlatform(p))
    .filter(Boolean)
    .filter(p => textFor(p!.id, content, variants).length > p!.charLimit) as NonNullable<
    ReturnType<typeof getPlatform>
  >[];

/**
 * Trim the caption to the last sentence that fits, as a starting point.
 *
 * Cut mid-word and a caption reads as broken; cut at a sentence and it reads as written.
 * Always editable afterwards — nothing auto-trimmed is published unreviewed.
 */
export const shortenFor = (content: string, cap: number): string => {
  const text = content.trim();
  if (text.length <= cap) return text;
  const head = text.slice(0, cap);
  const sentence = Math.max(head.lastIndexOf('. '), head.lastIndexOf('! '), head.lastIndexOf('? '));
  if (sentence > cap * 0.4) return head.slice(0, sentence + 1);
  const line = head.lastIndexOf('\n');
  if (line > cap * 0.4) return head.slice(0, line).trim();
  const word = head.lastIndexOf(' ');
  return (word > 0 ? head.slice(0, word) : head).trim();
};

/**
 * Per-platform captions, for any platform that is over its limit or already has its own text.
 *
 * X allows 280 where LinkedIn allows 3,000, so one shared caption forces the longest post down
 * to the shortest limit. A platform with its own version is measured against that instead.
 *
 * Shared by the composer and the edit dialog. The edit dialog originally had none of this: it
 * measured the shared caption against every platform's limit, so a post with a long body and a
 * short X version could not be saved at all — and it never sent the versions back, so the
 * server's `content_variants = ?` wrote NULL and the X text was destroyed on save.
 */
export const PerPlatformCopy: React.FC<Props> = ({ platforms, content, variants, onChange }) => {
  const configs = platforms
    .map(p => getPlatform(p))
    .filter(Boolean) as NonNullable<ReturnType<typeof getPlatform>>[];

  const shown = configs.filter(
    p => textFor(p.id, content, variants).length > p.charLimit || variants[p.id]?.trim()
  );
  if (!shown.length) return null;

  const set = (id: string, text: string) => onChange({ ...variants, [id]: text });
  const clear = (id: string) => {
    const next = { ...variants };
    delete next[id];
    onChange(next);
  };

  return (
    <>
      {shown.map(p => {
        const own = variants[p.id] ?? '';
        const editing = Boolean(own);
        const count = textFor(p.id, content, variants).length;

        return (
          <Box
            key={`variant-${p.id}`}
            sx={{ mt: 1.5, pl: 1.5, borderLeft: 3, borderColor: getPlatformColor(p.id) }}
          >
            {editing ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {p.name} version
                  </Typography>
                  <Typography
                    variant="caption"
                    color={count > p.charLimit ? 'error' : 'text.secondary'}
                  >
                    {count.toLocaleString()}/{p.charLimit.toLocaleString()}
                  </Typography>
                  <Button size="small" sx={{ ml: 'auto', minWidth: 0 }} onClick={() => clear(p.id)}>
                    Use the main caption
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  value={own}
                  onChange={e => set(p.id, e.target.value)}
                  error={count > p.charLimit}
                  placeholder={`What goes out on ${p.name}`}
                />
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="error">
                  {(count - p.charLimit).toLocaleString()} over {p.name}'s limit
                </Typography>
                <Button size="small" onClick={() => set(p.id, shortenFor(content, p.charLimit))}>
                  Write a shorter {p.name} version
                </Button>
              </Box>
            )}
          </Box>
        );
      })}
    </>
  );
};
